import { useEffect } from "react";
import confetti from "canvas-confetti";
import { toast } from "sonner";
import { useTheme } from "@/lib/ThemeContext";

/**
 * One-shot confetti celebration on the user's first-ever task
 * completion. Listens for the `mmntm:celebrate-first-task` CustomEvent
 * fired by the global axios interceptor when any response carries
 * `first_completion_celebration: true` (set by the backend in
 * `utils.first_task.consume_first_task_celebration`).
 *
 * Burst is short (~1.2s) and tasteful — three small bursts from below
 * the centre, themed to the user's accent color when available so the
 * celebration matches whatever theme they've picked.
 */
export default function FirstTaskCelebrate() {
  const { tokens: t } = useTheme();

  useEffect(() => {
    const onCelebrate = () => {
      // Sonner toast first so it sits above the canvas
      toast.success("Nice — first task complete! Momentum is real.", {
        duration: 4500,
      });

      const accent = t?.accent || "#A855F7";
      const palette = [accent, "#F59E0B", "#10B981", "#EC4899", "#FFFFFF"];

      const fire = (originX, particleRatio, opts) => {
        confetti({
          particleCount: Math.floor(120 * particleRatio),
          origin: { x: originX, y: 0.78 },
          colors: palette,
          ticks: 200,
          scalar: 0.9,
          ...opts,
        });
      };

      // Centre burst
      fire(0.5, 0.35, { spread: 26, startVelocity: 55 });
      // Left and right side fans for depth
      fire(0.3, 0.25, { spread: 60, angle: 120, startVelocity: 45 });
      fire(0.7, 0.25, { spread: 60, angle: 60, startVelocity: 45 });
      // A trailing low-velocity sprinkle so it doesn't end abruptly
      setTimeout(() => fire(0.5, 0.2, { spread: 100, startVelocity: 28, decay: 0.92 }), 220);
    };

    window.addEventListener("mmntm:celebrate-first-task", onCelebrate);
    return () => window.removeEventListener("mmntm:celebrate-first-task", onCelebrate);
  }, [t?.accent]);

  return null;
}
